// src/components/PaymentsPage.js
import React, { useState, useEffect } from "react";
import {
  collection, addDoc, getDocs, updateDoc,
  deleteDoc, doc, getDoc
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../firebase";

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);

  const [role, setRole] = useState("staff");
  const [roleLoading, setRoleLoading] = useState(true);

  const [form, setForm] = useState({
    tenantName: "",
    roomNo: "",
    amount: "",
    method: "Cash",
    date: "",
    status: "Paid",
  });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setRole("staff");
        setRoleLoading(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        setRole(snap.exists() ? snap.data().role : "staff");
      } catch {
        setRole("staff");
      }
      setRoleLoading(false);
    });
    return () => unsub();
  }, []);

  const canEdit = role === "superadmin" || role === "admin";

  const fetchData = async () => {
    const paySnap = await getDocs(collection(db, "payments"));
    const tenantSnap = await getDocs(collection(db, "tenants"));
    setPayments(paySnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setTenants(tenantSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredPayments = payments.filter((p) =>
    `${p.tenantName} ${p.roomNo} ${p.amount}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Payments</h1>
          {!canEdit && (
            <p className="text-xs text-gray-500">
              Read-only access – contact admin for changes.
            </p>
          )}
        </div>

        {canEdit && (
          <button
            className="px-4 py-2 bg-[#b4642d] text-white rounded hover:opacity-90"
            onClick={() => {
              setEditId(null);
              setForm({
                tenantName: "",
                roomNo: "",
                amount: "",
                method: "Cash",
                date: "",
                status: "Paid",
              });
              setShowModal(true);
            }}
          >
            + Add Payment
          </button>
        )}
      </div>

      {/* Search */}
      <input
        className="border p-2 w-64 mb-4 rounded"
        placeholder="Search tenant / room / amount"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* Table */}
      <div className="bg-white/95 border border-amber-50 rounded-xl shadow-md overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-orange-50">
            <tr className="text-left">
              {["Tenant", "Room", "Amount", "Method", "Date", "Status", "Actions"].map((h) => (
                <th key={h} className="p-3 font-semibold text-gray-700">
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filteredPayments.map((p) => (
              <tr key={p.id} className="hover:bg-orange-50/40">
                <td className="p-3">{p.tenantName}</td>
                <td className="p-3">{p.roomNo}</td>
                <td className="p-3">
                  ₦{p.amount ? Number(p.amount).toLocaleString() : "0"}
                </td>
                <td className="p-3">{p.method}</td>
                <td className="p-3">{p.date}</td>
                <td className="p-3">
                  <span
                    className={`px-2 py-1 rounded text-white text-xs ${
                      p.status === "Paid" ? "bg-green-600" : "bg-yellow-600"
                    }`}
                  >
                    {p.status}
                  </span>
                </td>
                <td className="p-3 space-x-3">
                  {canEdit ? (
                    <>
                      <button
                        className="text-[#b4642d] hover:underline"
                        onClick={() => {
                          setEditId(p.id);
                          setForm(p);
                          setShowModal(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="text-red-600 hover:underline"
                        onClick={() => {
                          if (window.confirm("Delete payment?")) {
                            deleteDoc(doc(db, "payments", p.id));
                            fetchData();
                          }
                        }}
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-gray-400 italic">View only</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center">
          <div className="bg-white/95 border border-amber-50 rounded-xl p-6 w-96 shadow-lg">
            <h2 className="font-bold text-lg mb-4">
              {editId ? "Edit Payment" : "New Payment"}
            </h2>

            <label className="block text-sm mb-1">Tenant</label>
            <select
              className="border w-full p-2 mb-3 rounded"
              value={form.tenantName}
              onChange={(e) => {
                const tenant = tenants.find(
                  (t) => t.fullName === e.target.value
                );
                setForm({
                  ...form,
                  tenantName: e.target.value,
                  roomNo: tenant?.assignedRoom || "",
                });
              }}
            >
              <option value="">Select Tenant</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.fullName}>
                  {t.fullName}
                </option>
              ))}
            </select>

            <label className="block text-sm mb-1">Room</label>
            <input
              className="border w-full p-2 mb-3 rounded bg-gray-100"
              value={form.roomNo}
              readOnly
            />

            <label className="block text-sm mb-1">Amount</label>
            <input
              className="border w-full p-2 mb-3 rounded"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />

            <label className="block text-sm mb-1">Method</label>
            <select
              className="border w-full p-2 mb-3 rounded"
              value={form.method}
              onChange={(e) => setForm({ ...form, method: e.target.value })}
            >
              <option>Cash</option>
              <option>Transfer</option>
              <option>POS</option>
            </select>

            <label className="block text-sm mb-1">Date</label>
            <input
              type="date"
              className="border w-full p-2 mb-3 rounded"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />

            <label className="block text-sm mb-1">Status</label>
            <select
              className="border w-full p-2 mb-4 rounded"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option>Paid</option>
              <option>Pending</option>
            </select>

            <div className="flex justify-end space-x-3">
              <button onClick={() => setShowModal(false)}>Cancel</button>
              <button
                className="px-4 py-2 bg-[#b4642d] text-white rounded hover:opacity-90"
                onClick={async () => {
                  if (!form.tenantName || !form.amount || !form.date) {
                    alert("Please fill all required fields");
                    return;
                  }

                  if (editId) {
                    await updateDoc(doc(db, "payments", editId), form);
                  } else {
                    await addDoc(collection(db, "payments"), form);
                  }

                  setShowModal(false);
                  fetchData();
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
